import { useCallback, useEffect, useRef, useState } from 'react';
import {
  CreateCardError,
  createCards,
  DEFAULT_RETRY_AFTER_MS,
  saveManualCard,
  type CreateCardArgs,
  type SaveManualCardArgs,
} from '@features/CardsWorkspace';
import {
  fetchGeneration,
  GenerationNotFoundError,
  isReusedManual,
  type GenerationDetail,
  type GenerationSummary,
} from '@entities/Generation';
import { newUuid } from '@shared/lib';
import { useNetworkStore } from '@shared/network';
import { CHECK_RETRY_MS, checkStep, pressSignature, type PressBody } from '../lib/composerModel';

/** Что знает страница о нажатии — для событий аналитики и списания (ТЗ § 17). */
export interface PressMeta {
  via: 'enter' | 'click';
  /** Нажатие спишет жетон (цена была на кнопке). */
  paid: boolean;
  cards: number;
  picks: number;
  /** Модуль по умолчанию, выбранный в чипе или только что созданный. */
  origin: 'default' | 'changed' | 'new';
}

export type CreatePress =
  | { kind: 'cards'; args: Omit<CreateCardArgs, 'generationId' | 'generationPart'>; meta: PressMeta }
  | { kind: 'manual'; args: Omit<SaveManualCardArgs, 'generationId'>; meta: PressMeta };

export interface CreatedCard {
  cardId: string;
  front: string;
  /** Карточка ещё генерируется — её «готово» соберёт итоговый тост. */
  generating: boolean;
}

export interface CreatedPress {
  press: CreatePress;
  /** Запись нажатия в «Генерациях» (ручная на старом ответе может прийти без неё). */
  generation: GenerationSummary | null;
  cards: CreatedCard[];
  /** Успех подтверждён проверкой после обрыва связи. */
  viaCheck: boolean;
  /** Когда ушёл запрос, принёсший сводку: SSE о той же записи, пришедшее раньше ответа, может быть свежее. */
  requestedAt: number;
  /** Ручная карточка: записи, где она сняла ожидание своих упавших карточек того же слова («удалена»);
   *  `null` — ответ о них не сказал (старый бэкенд, проверка после обрыва связи). */
  released: GenerationSummary[] | null;
  /** «Добавить» привязал свою карточку слова из другого модуля, введённый перевод не применился (A2-03):
   *  `moduleName` — где слово уже было (`null` — сервер не сказал). Не так или не ручная — нет поля или `null`. */
  reused?: { moduleName: string | null } | null;
}

export type FlowPhase = 'idle' | 'creating' | 'checking';

/** Строка над кнопкой (ТЗ § 10). */
export type FlowNotice =
  /** Ответ с ошибкой: текст по коду. */
  | { kind: 'failed'; error: CreateCardError }
  /** `generation: null` — ничего не создано: уже учишь или нет в словаре. */
  | { kind: 'nothingAdded' }
  /** «Связь прервалась — проверяем, создались ли карточки…» */
  | { kind: 'checking' }
  /** Проверка сама не прошла — ждём связь. */
  | { kind: 'checkOffline' }
  /** 404: запрос не дошёл или ничего не создал. */
  | { kind: 'notCreated' }
  /** 20 с без итога — нажать ещё раз безопасно: тот же id, второй жетон не спишется. */
  | { kind: 'notConfirmed' };

interface Callbacks {
  onCreated: (result: CreatedPress) => void;
  /**
   * Ученик ушёл с главной, пока шёл POST, а нажатие удалось: виджета уже нет, но то, что от него не зависит
   * (запись и слежение, один итоговый тост на нажатие, квота, модуль по умолчанию), всё равно делается.
   */
  onCreatedAway?: (result: CreatedPress) => void;
  /** Ответ с ошибкой; `unknownOutcome` — исход неизвестен, дальше проверка. */
  onFailed?: (error: CreateCardError, unknownOutcome: boolean) => void;
  onCheckResult?: (result: 'created' | 'not_created' | 'unknown') => void;
  /** `generation: null` — ничего не создано: страница перечитывает ответ словаря (метки «учишь», ТЗ § 8). */
  onNothingAdded?: (press: CreatePress) => void;
}

function bodyOf(press: CreatePress): PressBody {
  if (press.kind === 'manual') {
    return { word: press.args.front, moduleId: press.args.moduleId, translation: press.args.translations.join('|') };
  }
  return { word: press.args.front, moduleId: press.args.moduleId, picks: press.args.picks, phrases: press.args.phrases };
}

const isUnknownOutcome = (code: string) => code === 'NETWORK' || code === 'SERVER' || code === 'GENERATION_PART_IN_PROGRESS';

const wait = (ms: number) => new Promise<void>((resolve) => window.setTimeout(resolve, ms));

function summaryOf(detail: GenerationDetail): GenerationSummary {
  // Деталь — сводка плюс элементы; в кэш списка и в слежение уходит только сводка.
  const { items: _items, ...summary } = detail;
  return summary;
}

function cardsOf(detail: GenerationDetail): CreatedCard[] {
  return detail.items
    .filter((item) => item.cardId)
    .map((item) => ({
      cardId: item.cardId!,
      front: item.frontName,
      generating: item.status === 'generating' || item.status === 'extending',
    }));
}

/**
 * Создание со строки главной (ТЗ главной § 10): `generation_id` — новый на каждое нажатие; после
 * неизвестного исхода (нет ответа, 5xx, «тот же запрос ещё выполняется») POST сам не повторяется —
 * исход проверяется по `GET /generations/{id}`. Нажали ещё раз с тем же телом — уходит тот же id:
 * сервер ответит прежним ответом (`replayed`), без второго жетона и без дублей.
 */
export function useCreateFlow(callbacks: Callbacks) {
  const [phase, setPhaseState] = useState<FlowPhase>('idle');
  const [notice, setNotice] = useState<FlowNotice | null>(null);
  const phaseRef = useRef<FlowPhase>('idle');
  const cb = useRef(callbacks);
  cb.current = callbacks;
  // Неизвестный исход: тело и id — следующее нажатие с тем же телом уйдёт с тем же id.
  const unknown = useRef<{ signature: string; pressId: string } | null>(null);
  // Текущая проверка; смена номера — отмена (✕, правка слова, уход с главной).
  const run = useRef(0);
  const mounted = useRef(true);
  const pending = useRef<{ press: CreatePress; pressId: string; startedAt: number } | null>(null);
  const waitingNetwork = useRef(false);
  const network = useNetworkStore((s) => s.status);

  const setPhase = useCallback((next: FlowPhase) => {
    phaseRef.current = next;
    setPhaseState(next);
  }, []);

  const finish = useCallback(
    (result: CreatedPress) => {
      pending.current = null;
      waitingNetwork.current = false;
      setPhase('idle');
      setNotice(null);
      cb.current.onCreated(result);
    },
    [setPhase],
  );

  const check = useCallback(
    async (token: number, delay: number) => {
      const current = pending.current;
      if (!current) return;
      if (delay > 0) {
        await wait(delay);
        if (token !== run.current) return;
      }
      let result: GenerationDetail | 'not_found';
      const requestedAt = Date.now();
      try {
        result = await fetchGeneration(current.pressId);
      } catch (err) {
        if (token !== run.current) return;
        if (!(err instanceof GenerationNotFoundError)) {
          // Проверка сама не прошла: ждём связь, кнопка неактивна (ТЗ § 10, п. 3).
          waitingNetwork.current = true;
          setNotice({ kind: 'checkOffline' });
          return;
        }
        result = 'not_found';
      }
      if (token !== run.current) return;
      const step = checkStep(
        result === 'not_found' ? 'not_found' : { processing: result.processing, total: result.counts.total },
        Date.now() - current.startedAt,
      );
      if (step === 'wait') {
        void check(token, CHECK_RETRY_MS);
        return;
      }
      if (step === 'created' && result !== 'not_found') {
        unknown.current = null;
        cb.current.onCheckResult?.('created');
        finish({
          press: current.press,
          generation: summaryOf(result),
          cards: cardsOf(result),
          viaCheck: true,
          requestedAt,
          released: null,
          reused: current.press.kind === 'manual' && isReusedManual(result) ? { moduleName: null } : null,
        });
        return;
      }
      // Не создалось или не подтвердилось за 20 с — кнопка снова активна, id прежний.
      pending.current = null;
      setPhase('idle');
      cb.current.onCheckResult?.(step === 'timeout' ? 'unknown' : 'not_created');
      setNotice({ kind: step === 'timeout' ? 'notConfirmed' : 'notCreated' });
    },
    [finish, setPhase],
  );

  const submit = useCallback(
    async (press: CreatePress) => {
      if (phaseRef.current !== 'idle') return;
      const signature = pressSignature(bodyOf(press));
      const known = unknown.current;
      const pressId = known && known.signature === signature ? known.pressId : newUuid();
      const token = ++run.current;
      setPhase('creating');
      setNotice(null);
      const requestedAt = Date.now();
      /** Ответ пришёл, а виджета уже нет (ушёл с главной, пока шёл POST) — только то, что от него не зависит. */
      const away = (result: CreatedPress | null) => {
        if (!mounted.current && result) cb.current.onCreatedAway?.(result);
      };
      try {
        if (press.kind === 'cards') {
          const res = await createCards({ ...press.args, generationId: pressId });
          const result: CreatedPress | null = res.generation
            ? {
                press,
                generation: res.generation,
                cards: res.cards.map((card) => ({
                  cardId: card.cardId,
                  front: card.payload.front,
                  generating: card.status === 'queued' || card.status === 'generating',
                })),
                viaCheck: false,
                requestedAt,
                released: null,
              }
            : null;
          if (token !== run.current) {
            away(result);
            return;
          }
          unknown.current = null;
          if (!result) {
            // Ничего не создано — не успех: лист остаётся, ответ словаря перечитывается (ТЗ § 8).
            setPhase('idle');
            setNotice({ kind: 'nothingAdded' });
            cb.current.onNothingAdded?.(press);
            return;
          }
          finish(result);
        } else {
          const res = await saveManualCard({ ...press.args, generationId: pressId });
          // Сервер не сказал `reused` — судим по записи: у переиспользованной карточки элемент без выбора.
          const reused = res.reused ?? (res.generation !== null && isReusedManual(res.generation));
          const result: CreatedPress = {
            press,
            generation: res.generation,
            cards: [{ cardId: res.card.cardId, front: res.card.payload.front, generating: false }],
            viaCheck: false,
            requestedAt,
            released: res.released,
            reused: reused ? { moduleName: res.reusedModule?.name ?? null } : null,
          };
          if (token !== run.current) {
            away(result);
            return;
          }
          unknown.current = null;
          finish(result);
        }
      } catch (err) {
        if (token !== run.current) return;
        const error = err instanceof CreateCardError ? err : new CreateCardError('UNKNOWN');
        if (isUnknownOutcome(error.code)) {
          unknown.current = { signature, pressId };
          pending.current = { press, pressId, startedAt: Date.now() };
          setPhase('checking');
          setNotice({ kind: 'checking' });
          cb.current.onFailed?.(error, true);
          void check(token, error.code === 'GENERATION_PART_IN_PROGRESS' ? (error.retryAfterMs ?? DEFAULT_RETRY_AFTER_MS) : 0);
          return;
        }
        unknown.current = null;
        setPhase('idle');
        setNotice({ kind: 'failed', error });
        cb.current.onFailed?.(error, false);
      }
    },
    [check, finish, setPhase],
  );

  /** ✕ и правка слова отменяют ожидание проверки; строка над кнопкой уходит. */
  const cancel = useCallback(() => {
    if (phaseRef.current === 'creating') return;
    run.current += 1;
    pending.current = null;
    waitingNetwork.current = false;
    setPhase('idle');
    setNotice(null);
  }, [setPhase]);

  const clearNotice = useCallback(() => setNotice(null), []);

  // Связь вернулась — проверяем исход дальше (без нового POST).
  useEffect(() => {
    if (network !== 'reconnected' || !waitingNetwork.current || !pending.current) return;
    waitingNetwork.current = false;
    setNotice({ kind: 'checking' });
    void check(run.current, 0);
  }, [network, check]);

  // Уход с главной — никаких ответов в размонтированный виджет (кроме `onCreatedAway`).
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      run.current += 1;
    };
  }, []);

  return { phase, notice, submit, cancel, clearNotice };
}
